import React, { useState } from 'react';
import { Info } from 'lucide-react';
import type { Question, normalizeAnswerStyle } from '../../../types/publicForm.types';
import { useFormContext } from './FormContext';
import { TextQuestion } from './questions/TextQuestion';
import { TextareaQuestion } from './questions/TextareaQuestion';
import { SelectQuestion } from './questions/SelectQuestion';
import { RadioQuestion } from './questions/RadioQuestion';
import { CheckboxQuestion } from './questions/CheckboxQuestion';
import { FileUploadQuestion } from './questions/FileUploadQuestion';
import { TagQuestion } from './questions/TagQuestion';
import { ConditionalQuestions } from './ConditionalQuestions';

interface FormQuestionProps {
  question: Question;
  primaryColor: string;
}

export const FormQuestion: React.FC<FormQuestionProps> = ({ question, primaryColor }) => {
  const { state, actions, formData } = useFormContext();
  const [expandedMoreInfo, setExpandedMoreInfo] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const value = state.formData[question.id] || '';
  const isTouched = state.touchedFields.has(question.id);
  const error = state.validationErrors[question.id];

  // Check if we should show validation error
  const showValidationError = question.required && isTouched && !value;

  // Map answer styles to normalized types
  const getNormalizedStyle = (style: string): string => {
    const mappings: Record<string, string> = {
      'dropdown': 'select',
      'multiple_choice': 'radio',
      'checkboxes': 'checkbox',
      'long_text': 'textarea',
      'short_text': 'text',
      'paragraph': 'textarea',
      'multiselect': 'multiselect',
      'file': 'file',
      'tag_question': 'tag_question',
    };
    return mappings[style] || style;
  };

  const normalizedStyle = getNormalizedStyle(question.answerStyle);

  // Debug logging for tag questions
  if (question.answerStyle === 'tag_question' || normalizedStyle === 'tag_question') {
    console.log('Tag Question Debug:', {
      questionId: question.id,
      answerStyle: question.answerStyle,
      normalizedStyle,
      options: question.options,
      value: state.formData[question.id]
    });
  }

  // Handle input change with conditional question logic
  const handleInputChange = (value: any) => {
    actions.updateField(question.id, value);

    // Handle conditional questions
    if (hasConditionalQuestions(question)) {
      updateConditionalQuestionVisibility(question, value);
    }
  };

  const hasConditionalQuestions = (q: Question): boolean => {
    if (!q.answers || !q.showAnswerOptions) return false;
    return q.answers.some(answer =>
      answer.childQuestions && answer.childQuestions.length > 0 && !answer._destroy
    );
  };

  const updateConditionalQuestionVisibility = (q: Question, value: any) => {
    const newVisibleChildQuestions = new Set(state.visibleChildQuestions);

    // First, remove all child questions for this parent question
    q.answers?.forEach(answer => {
      answer.childQuestions?.forEach(childQ => {
        newVisibleChildQuestions.delete(childQ.id);
      });
    });

    // Then, add child questions for selected answers
    if (value) {
      const selectedAnswerIds = Array.isArray(value) ? value : [value];

      selectedAnswerIds.forEach(answerId => {
        const answer = q.answers?.find(a => String(a.id) === String(answerId));
        if (answer?.childQuestions) {
          answer.childQuestions.forEach(childQ => {
            if (!childQ._destroy) {
              newVisibleChildQuestions.add(childQ.id);
            }
          });
        }
      });
    }

    actions.setVisibleChildQuestions(newVisibleChildQuestions);
  };

  const renderQuestion = () => {
    // Add helpText and moreInfo to the question object for components to use
    const questionWithHelpers = {
      ...question,
      description: question.helpText, // Map helpText to description for QuestionFooter
    };

    const commonProps = {
      question: questionWithHelpers,
      value,
      onChange: handleInputChange,
      onBlur: () => actions.markFieldTouched(question.id),
      error: showValidationError ? 'This field is required' : error,
      primaryColor,
      moreInfo: question.moreInfo,
      isMoreInfoExpanded: expandedMoreInfo,
      onToggleMoreInfo: question.moreInfo ? () => setExpandedMoreInfo(!expandedMoreInfo) : undefined,
    };

    switch (normalizedStyle) {
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
      case 'date':
        return <TextQuestion {...commonProps} type={normalizedStyle} />;

      case 'textarea':
        return <TextareaQuestion {...commonProps} />;

      case 'select':
        // Check if it's a multi-select dropdown based on allowMultipleAnswers
        const isMultiSelect = question.allowMultipleAnswers === true;
        console.log('isMultiSelect:', isMultiSelect);
        return <SelectQuestion {...commonProps} isMulti={isMultiSelect} onDropdownToggle={setIsDropdownOpen} />;

      case 'multiselect':
        return <SelectQuestion {...commonProps} isMulti={true} onDropdownToggle={setIsDropdownOpen} />;

      case 'radio':
        return <RadioQuestion {...commonProps} />;

      case 'checkbox':
        return <CheckboxQuestion {...commonProps} />;

      case 'file':
        return <FileUploadQuestion {...commonProps} />;

      case 'tag_question':
        // For tag questions, we need to pass available tags and qualifications
        // These would typically be provided in the form data or fetched separately
        // Ensure value is an object for tag questions
        const tagValue = typeof value === 'object' && value !== null ? value : {
          tagIds: [],
          tagRequests: [],
          questionResponseTaggings: []
        };
        return (
          <TagQuestion
            {...commonProps}
            value={tagValue}
            availableTags={[]} // TODO: These should be provided by the form context
            availableQualifications={[]} // TODO: These should be provided by the form context
            teamSlug={formData?.teamSlug}
          />
        );

      default:
        return (
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">
                  Unsupported Question Type
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  The answer style "{question.answerStyle}" is not yet supported.
                </p>
              </div>
            </div>
          </div>
        );
    }
  };

  const renderCommentField = () => {
    const commentId = `${question.id}_comment`;
    const commentValue = state.formData[commentId] || '';

    return (
      <div className="mt-4">
        <label
          htmlFor={commentId}
          className="block text-sm font-medium text-gray-600 mb-2"
        >
          Additional Comments (Optional)
        </label>
        <textarea
          id={commentId}
          placeholder="Please provide any additional comments..."
          value={commentValue}
          onChange={(e) => actions.updateField(commentId, e.target.value)}
          rows={3}
          className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:ring-2 focus:border-transparent"
          style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
        />
      </div>
    );
  };

  return (
    <div className={`space-y-4 mt-8 mb-12 relative ${isDropdownOpen ? 'z-[1000]' : ''}`}>
      {/* Question Label */}
      <label
        htmlFor={question.id}
        className="block font-medium"
      >
        {question.name}
        {question.required && (
          <span className="text-red-500 ml-1">*</span>
        )}
      </label>

      {/* Question Input with min-height for dropdown expansion */}
      <div className="min-h-[50px] relative">
        {renderQuestion()}
      </div>

      {/* Comment Field */}
      {question.showComments && renderCommentField()}

      {/* Conditional Questions */}
      {hasConditionalQuestions(question) && (
        <ConditionalQuestions
          parentQuestion={question}
          selectedValue={value}
          primaryColor={primaryColor}
        />
      )}
    </div>
  );
};